import React, { ChangeEvent, FC, useRef } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import { Pencil } from "lucide-react";
import { Button } from "src/components/ui/button";

interface Props {
	stepNumber: number;
	control: any;
	setValue: UseFormSetValue<BannerFormState>;
	index: number;
	mediasPreview: Media[];
	setMediasPreview: React.Dispatch<React.SetStateAction<Media[]>>;
	uploading: boolean;
	error?: boolean;
	upload: (file: File, uploadType: string, uploadIndex?: number) => void;
	isVideo?: boolean;
}

const EditMedia: FC<Props> = ({
	stepNumber,
	control,
	index,
	mediasPreview,
	setMediasPreview,
	uploading,
	upload,
	error,
	isVideo
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		field: { onChange: uuidOnChange, value: uuidValue, ...inputProps }
	} = useController({
		name: `steps.${stepNumber - 1}.value`,
		control,

		rules: { required: true }
	});

	const medias: Media[] = useWatch({
		name: `steps.${stepNumber - 1}.mediasPreview`,
		control
	});

	const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;

		if (!target) return;
		if (!target.files) return;

		const selectedFiles = Array.from(target.files);

		if (selectedFiles.length > 3) {
			return alert(
				"You are only allowed to upload a maximum of 3 files at a time"
			);
		}

		if (
			selectedFiles.filter((file) => file.type.includes("video")).length +
				medias.filter((file) => file.type.includes("video")).length >
				1 &&
			!isVideo
		) {
			return alert("Only one video can be added to the post");
		}

		selectedFiles.forEach((file) => {
			const reader = new FileReader();

			reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
				if (!progressEvent) return;

				if (!progressEvent.target) return;

				const newIds = mediasPreview.slice();

				newIds[index].preview = progressEvent!.target!.result as string;
				newIds[index].type = file.type.includes("video") ? "video" : "image";
				newIds[index].file = file.name;
				newIds[index].value = "";
				newIds[index].id = Math.random();

				setMediasPreview(newIds);

				if (file && file.type.includes("image")) {
					upload(file, "images", index);
				}

				if (file && file.type.includes("video")) {
					upload(file, "video", index);
				}
			};

			reader.readAsDataURL(file);
		});
	};

	return (
		<div>
			<input
				type="file"
				onChange={onFileSelected}
				accept="image/jpeg,image/png,video/mp4,video/quicktime"
				{...inputProps}
				name={`steps.${stepNumber - 1}.value`}
				ref={inputRef}
				style={{ display: "none" }}
				aria-label="file-upload"
			/>

			<Button
				type="button"
				variant="ghost"
				size="icon"
				onClick={() => inputRef?.current?.click()}
				disabled={uploading || error}
			>
				<Pencil size={18} />
			</Button>
		</div>
	);
};

export default EditMedia;
